import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { pendoService } from '../services/pendo';

/**
 * Hook to manage Pendo initialization based on authentication state
 * 
 * Automatically initializes Pendo when user is authenticated
 * Following Pendo best practices: initialize after user data is available
 */
export const usePendo = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user || hasInitialized.current) {
      return;
    }

    // Set flag immediately to prevent race condition
    hasInitialized.current = true;

    const initializePendo = async () => {
      try {
        // Determine environment
        const environment = process.env.NODE_ENV === 'production' 
          ? (window.location.hostname.includes('staging') ? 'staging' : 'production')
          : 'development';

        // Prepare visitor data (required)
        // visitor.id must be a unique, stable identifier
        const visitor = {
          id: user.sub || user.email || '',
          email: user.email,
          full_name: user.name,
          environment: environment,
        };

        // Prepare account data (optional but recommended for B2B apps)
        // Links this user to their organization in Pendo
        const account = user.rootOrganisationId ? {
          id: String(user.rootOrganisationId),
          name: user.rootOrganisationName,
          environment: environment,
        } : undefined;

        // Initialize Pendo
        await pendoService.initialize(visitor, account);

        // Optional: Track successful login event
        pendoService.trackEvent('user_logged_in', {
          organisation: user.rootOrganisationName,
          organisation_id: user.rootOrganisationId,
          login_timestamp: new Date().toISOString(),
        });
      } catch (error) {
        // Pendo initialization failure should not break the app
        // Error is already logged in pendoService
        hasInitialized.current = false;
      }
    };

    initializePendo();
  }, [user, isAuthenticated, isLoading]);

  // Return Pendo service methods for custom tracking in components
  return {
    trackEvent: pendoService.trackEvent.bind(pendoService),
    isReady: pendoService.isReady.bind(pendoService),
  };
};
